import { useState } from "react";
import { EntryField, EntryTitle, EntryWrapper } from "../../../components/customStyles/form";
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, InputAdornment, IconButton, Typography } from "@mui/material";
import { Visibility, VisibilityOff, Key } from "@mui/icons-material"
import { useTranslation } from "react-i18next";
import userService from "../../../services/user/UserService";
import auth from "../../../services/auth/AuthService";

interface RevalidateUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    postValidation: () => void;
}

const RevalidateUserModal = ({ isOpen, onClose, postValidation }: RevalidateUserModalProps) => {

    const { t } = useTranslation();

    const [pwd, setPwd] = useState("");
    const [isShowPassword, setIsShowPassword] = useState(false);
    const [isError, setIsError] = useState(false);

    const onClickShowPassword = () => setIsShowPassword((show) => !show);

    const onVerifyPassword = () => {
        const user = auth.user;
        if (!user) return;

        userService.PostRevalidateUser(user.email, pwd).then((isValid) => {
            if (isValid) {
                postValidation()
                onClose()
                setPwd("")
                return;
            }
            setIsError(true)
        })
    }

    return (
        <Dialog
            open={isOpen}
            onClose={() => {
                onClose()
                setPwd("")
            }}
        >

            <DialogTitle>
                {t("login.verification")}
            </DialogTitle>

            <DialogContent>
                <DialogContentText>
                    {t("login.verifyDesc")}
                </DialogContentText>

                <TextField
                    margin="normal"
                    required
                    fullWidth
                    size="medium"
                    label={t("login.password")}
                    autoComplete="off"
                    variant="outlined"
                    color={isError ? 'error' : 'primary'}
                    focused={isError ? true : undefined}
                    type={isShowPassword ? 'text' : 'password'}
                    value={pwd}
                    onChange={(event) => {
                        setIsError(false)
                        setPwd(event.target.value)
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {<Key sx={{ fontSize: 15 }} />}
                            </InputAdornment>
                        ),
                        endAdornment: (

                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={onClickShowPassword}
                                    edge="end"
                                >
                                    {isShowPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>

                        ),
                    }}
                />

                {isError &&
                    <Typography
                        style={{ color: "red", fontSize: 14 }}
                    >
                        {t("login.invalidPwd")}
                    </Typography>
                }
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={() => {
                        onClose()
                        setPwd("")
                    }}
                >
                    {t("header.cancel")}
                </Button>

                <Button
                    onClick={onVerifyPassword}
                    disabled={!pwd.length}
                >
                    {t("button.verify")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

interface LocalAlarmFieldProps {
    value?: string;
    onChange: (value: string) => void;
    getAlarmCode?: () => void;
}

const LocalAlarmField = ({ value, onChange, getAlarmCode }: LocalAlarmFieldProps) => {
    const { t } = useTranslation();

    const [isShowModal, setIsShowModal] = useState(false);
    const [isShowCode, setIsShowCode] = useState(false);

    const onClick = () => {
        if (!getAlarmCode) {
            setIsShowCode((old) => !old)
            return;
        }

        if (!isShowCode) setIsShowModal(true)
        else setIsShowCode(false)
    };

    return (
        <>
            <EntryWrapper>
                <EntryTitle>
                    {t("table.local.alarmCode")}
                </EntryTitle>
                <EntryField>

                    <TextField
                        type={isShowCode ? 'text' : 'password'}
                        disabled={getAlarmCode && !isShowCode}
                        variant="outlined"
                        required={false}
                        style={{ textAlign: "left", width: "100%" }}
                        value={!getAlarmCode || isShowCode ? (value ?? "") : "*******************************************"}
                        onChange={(e) => {
                            if (getAlarmCode && !isShowCode) return;
                            onChange(e.target.value)
                        }}
                        InputProps={{
                            endAdornment: (
                                <Button onClick={onClick}>
                                    {isShowCode ? <VisibilityOff /> : <Visibility />}
                                </Button>
                            ),
                        }}
                    />
                </EntryField>
            </EntryWrapper>
            <RevalidateUserModal
                isOpen={isShowModal}
                onClose={() => setIsShowModal(false)}
                postValidation={() => {
                    if (getAlarmCode) getAlarmCode()
                    setIsShowCode(true)
                }}
            />
        </>
    );
}

export default LocalAlarmField;